import React, { useState, useEffect, createContext, useContext } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => useContext(AccessibilityContext);

// Style-Sheet für Hoher Kontrast dynamisch einfügen
const injectHighContrastStyles = () => {
  const styleId = 'a11y-high-contrast-styles';
  
  // Entfernen falls vorhanden
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();
  
  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    html.a11y-high-contrast,
    html.a11y-high-contrast body,
    html.a11y-high-contrast div,
    html.a11y-high-contrast section,
    html.a11y-high-contrast header,
    html.a11y-high-contrast footer,
    html.a11y-high-contrast nav,
    html.a11y-high-contrast main,
    html.a11y-high-contrast article,
    html.a11y-high-contrast aside,
    html.a11y-high-contrast span,
    html.a11y-high-contrast p,
    html.a11y-high-contrast h1,
    html.a11y-high-contrast h2,
    html.a11y-high-contrast h3,
    html.a11y-high-contrast h4,
    html.a11y-high-contrast h5,
    html.a11y-high-contrast h6,
    html.a11y-high-contrast ul,
    html.a11y-high-contrast li {
      background-color: #000000 !important;
      color: #ffffff !important;
      border-color: #ffffff !important;
    }
    
    html.a11y-high-contrast a {
      color: #ffff00 !important;
      text-decoration: underline !important;
    }
    
    html.a11y-high-contrast button {
      background-color: #ffffff !important;
      color: #000000 !important;
      border: 2px solid #ffffff !important;
    }
    
    html.a11y-high-contrast img {
      filter: grayscale(100%) contrast(1.2) !important;
    }
  `;
  document.head.appendChild(style);
};

const removeHighContrastStyles = () => {
  const styleId = 'a11y-high-contrast-styles';
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();
};

// Große Schrift Styles
const injectLargeFontStyles = () => {
  const styleId = 'a11y-large-font-styles';
  
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();
  
  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    html.a11y-large-font {
      font-size: 140% !important;
    }
    
    html.a11y-large-font p,
    html.a11y-large-font span,
    html.a11y-large-font li,
    html.a11y-large-font a,
    html.a11y-large-font button {
      font-size: 1.25rem !important;
      line-height: 1.7 !important;
    }
    
    html.a11y-large-font h1 {
      font-size: 2.5rem !important;
    }
    
    html.a11y-large-font h2 {
      font-size: 2rem !important;
    }
    
    html.a11y-large-font h3 {
      font-size: 1.5rem !important;
    }
  `;
  document.head.appendChild(style);
};

const removeLargeFontStyles = () => {
  const styleId = 'a11y-large-font-styles';
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();
};

// Style-Manipulation
const applyHighContrast = (enabled) => {
  const html = document.documentElement;
  
  if (enabled) {
    html.classList.add('a11y-high-contrast');
    injectHighContrastStyles();
  } else {
    html.classList.remove('a11y-high-contrast');
    removeHighContrastStyles();
  }
};

const applyLargeFont = (enabled) => {
  const html = document.documentElement;
  
  if (enabled) {
    html.classList.add('a11y-large-font');
    injectLargeFontStyles();
  } else {
    html.classList.remove('a11y-large-font');
    removeLargeFontStyles();
  }
};

export const AccessibilityProvider = ({ children }) => {
  const [largeFont, setLargeFont] = useState(false);
  const [highContrast, setHighContrast] = useState(false);

  // Einstellungen aus localStorage laden
  useEffect(() => {
    const savedLargeFont = localStorage.getItem('a11y-large-font') === 'true';
    const savedHighContrast = localStorage.getItem('a11y-high-contrast') === 'true';
    
    setLargeFont(savedLargeFont);
    setHighContrast(savedHighContrast);
    
    // Direkt anwenden
    applyLargeFont(savedLargeFont);
    applyHighContrast(savedHighContrast);
  }, []);

  const toggleLargeFont = () => {
    const newValue = !largeFont;
    setLargeFont(newValue);
    localStorage.setItem('a11y-large-font', newValue.toString());
    applyLargeFont(newValue);
  };

  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    localStorage.setItem('a11y-high-contrast', newValue.toString());
    applyHighContrast(newValue);
  };

  const resetAll = () => {
    setLargeFont(false);
    setHighContrast(false);
    localStorage.removeItem('a11y-large-font');
    localStorage.removeItem('a11y-high-contrast');
    applyLargeFont(false);
    applyHighContrast(false);
  };

  return (
    <AccessibilityContext.Provider value={{
      largeFont,
      highContrast,
      toggleLargeFont,
      toggleHighContrast,
      resetAll
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityProvider;
